import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { DetailDrawer, DetailDrawerProps } from '@rossum/ui';
import { IconCirclePlus, IconEdit } from '@rossum/ui/icons/tabler';
import { CircularProgress, Stack, SvgIcon } from '@rossum/ui/material';
import { useTemplateTrigger } from '../hooks/useTemplateTrigger';
import { emailTemplatesEnabledSelector } from '../useEmailTemplatesEnabled';
import { EmailTemplateForm } from './EmailTemplateForm';
import { EmailTemplateDrawerAction } from './helpers';
import { TrialLimited } from './TrialLimited';

type EmailTemplateDrawerProps = {
  DrawerProps: Pick<DetailDrawerProps, 'open' | 'onClose'>;
  action: EmailTemplateDrawerAction | null;
  queueUrl: string;
};

const EmailTemplateDrawer = ({
  action,
  queueUrl,
  DrawerProps: { open, onClose },
}: EmailTemplateDrawerProps) => {
  const intl = useIntl();
  const actionTypeUpdate = action && action.type === 'update';

  const { data: trigger, isInitialLoading: isTriggerLoading } =
    useTemplateTrigger(
      actionTypeUpdate ? action.emailTemplate.triggers[0] : undefined
    );
  const emailTemplatesEnabled = useSelector(emailTemplatesEnabledSelector);

  const emailTemplateId = actionTypeUpdate
    ? action.emailTemplate.id
    : undefined;

  return (
    <DetailDrawer
      titleIcon={
        <SvgIcon fontSize="large">
          {actionTypeUpdate ? <IconEdit /> : <IconCirclePlus />}
        </SvgIcon>
      }
      title={intl.formatMessage({
        id: `components.editTemplateDrawer.${
          actionTypeUpdate ? 'edit' : 'create'
        }.title`,
      })}
      description={
        emailTemplateId
          ? intl.formatMessage(
              {
                id: 'components.editTemplateDrawer.edit.templateId',
              },
              { id: emailTemplateId }
            )
          : undefined
      }
      open={open}
      keepMounted={false}
      onClose={onClose}
    >
      <Stack spacing={2} sx={{ p: 3 }}>
        {!emailTemplatesEnabled && <TrialLimited />}
        {action && !isTriggerLoading ? (
          <EmailTemplateForm
            queueUrl={queueUrl}
            action={action}
            trigger={trigger ?? null}
            onClose={() => onClose?.({}, 'backdropClick')}
            disabled={!emailTemplatesEnabled}
          />
        ) : (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
            <CircularProgress size={48} />
          </Stack>
        )}
      </Stack>
    </DetailDrawer>
  );
};

export { EmailTemplateDrawer };
